/**
 * Reduce an axios rejection to a string that is always safe as a React child.
 *
 * Four wire shapes reach the UI and only one of them is a string. A FastAPI 422
 * makes `detail` an ARRAY of {type, loc, msg, input}; assigning that into state
 * and rendering it threw React #31 and took the whole Settings → Security page
 * (#603). The guard existed at ONE call site and not at the other four, which is
 * the argument for a helper rather than a fifth `typeof` check.
 *
 * Never returns a non-string: every branch ends at a string or the fallback.
 *
 *   | wire shape                          | source                              |
 *   |-------------------------------------|-------------------------------------|
 *   | "That code didn't work."            | raise HTTPException(detail="…")     |
 *   | [{type, loc, msg, input}, …]        | FastAPI request validation (422)    |
 *   | {code, message, …}                  | _code_error in routes/mfa.py (#594) |
 *   | undefined / null / anything else    | 5xx, network, opaque proxy errors   |
 */
export function errorText(err, fallback = 'Something went wrong. Please try again.') {
    const detail = err?.response?.data?.detail;

    if (typeof detail === 'string' && detail.trim()) return detail;

    // FastAPI request validation: detail is an array of error items.
    if (Array.isArray(detail)) {
        const msgs = detail
            .map((item) => (typeof item === 'string' ? item : item?.msg))
            .filter((m) => typeof m === 'string' && m.trim());
        if (msgs.length) return msgs.join('. ');
        return fallback;
    }

    // Classified MFA rejections (#594) and anything else object-shaped.
    if (detail && typeof detail === 'object') {
        const msg = detail.message ?? detail.detail ?? detail.msg;
        if (typeof msg === 'string' && msg.trim()) return msg;
        return fallback;
    }

    return fallback;
}

/**
 * Deliberately NOT done here, recorded so neither is re-added in review:
 *
 * - No `String(detail)` fallback. `String([{…}])` produces "[object Object]",
 *   which is worse than the fallback sentence for every reader.
 * - No read of `err.message` or the HTTP status. Call sites that already branch
 *   on `err?.response?.status` (change-password, MFA disable) keep doing so —
 *   this helper only replaces the unsafe `detail` read.
 */

/**
 * The CRM's `409 contact_exists` (#902): another live contact in the org
 * already uses this email (`POST /api/contacts`, `PUT /api/contacts/{id}`).
 * Returns `{ id, name, message }` so the caller can point at the existing
 * contact — `message` is always a string — or null for any other rejection.
 */
export function contactExists(err) {
    const detail = err?.response?.data?.detail;
    if (err?.response?.status !== 409 || !detail || typeof detail !== 'object'
        || detail.code !== 'contact_exists') return null;
    return {
        id: typeof detail.existing_contact_id === 'string' ? detail.existing_contact_id : null,
        name: typeof detail.existing_contact_name === 'string' ? detail.existing_contact_name : null,
        message: errorText(err, 'This email already belongs to another contact.'),
    };
}
